/* import __COLOCATED_TEMPLATE__ from './posting-results-list.hbs'; */
import { readOnly, alias } from '@ember/object/computed';
import { computed, observer } from '@ember/object';
import { task } from 'ember-concurrency';
import { A } from '@ember/array';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import { APPLICATION } from 'sh-www/models/application';
import { EMBER_ROUTE_NAMES } from 'sh-www/constants/routes';
import LightboxOverlay from 'sh-www/components/modals/overlays/lightbox';
import MakeOfferModal from 'sh-www/components/modals/posting/make-offer-modal';
import StripeCCModal from 'sh-www/components/modals/payments/stripe-cc-modal';
import DeclineApplicationModal from 'sh-www/components/modals/lsg/lsg-modal-text-editor';
import UndeclineApplicationModal from 'sh-www/components/modals/undecline-modal';
import ConfirmationModal from 'sh-www/components/modals/confirmation';
import { showModal } from 'sh-www/utils/modal-controls';

export default Component.extend({
  /**
   * HTML Component properties
   */
  tagName: 'div',
  classNames: ['SH-c-posting-results-list'],
  classNameBindings: ['state.isEmptyDefaultSearch:margin-top-32px'],

  /**
   * Register services
   */
  applicationCommandService : service('commands/application-commands'),
  applicationStateManager: service('state-managers/application'),
  errorTracker: service('error-tracker'),
  state: service('states/routes/posting'),
  stateManager: service('state-managers/routes/posting'),
  threadCommandService : service('commands/thread-commands'),
  session: service(),
  store: service(),

  currentUser: readOnly('session.currentUser'),

  /**
   * Array of of applications for the list view.
   *
   * @param {Ember.MutableArray<Application>}
   */
  applicationListItems: A(),

  applicationSimpleStatusFilter: null,

  currentUserCompanyTeams: [],

  featuredApplications: [],

  /** @param {Function} */
  loadApplications: () => {},

  /**
   * True when we have at least 1 application
   *
   * @type {Boolean}
   * @private
   */
  _hasListItems: computed('applicationListItems.[]', function() {
    return this.applicationListItems.length > 0;
  }),

  /**
   * True if we have NOT searched yet.  Different than a search with no results.
   *
   * @type {Boolean}
   */
  _hasNotSearchedYet: computed('state.applicationSearchResults', function() {
    let applications = this.get('state.applicationSearchResults');
    return !applications.get('isLoaded');
  }),

  /**
   * @type {Boolean}    True when we can show the quick actions sections that has 'message all' and 'decline all' for example.
   */
  _isCanShowQuickActions: computed(
  'state.pendingCount',
  'applicationSimpleStatusFilter',
  function() {
    return this.get('state.pendingCount') &&
      this.applicationSimpleStatusFilter === APPLICATION.SIMPLE_STATUS.PENDING;
  }),

  /**
   * True if we want to disable the checkbox.
   *
   * @type {Boolean}
   */
  _isCheckboxDisabled: computed('state.isLoadingAllPages', 'state.isSelectAllChecked', function() {
    return this.get('state.isLoadingAllPages') && this.get('state.isSelectAllChecked');
  }),

  /**
   * True when our current state is pointing to the 'declined' status
   *
   * @type {Boolean}
   */
  _isDeclined: computed('applicationSimpleStatusFilter', function() {
    return this.applicationSimpleStatusFilter === APPLICATION.SIMPLE_STATUS.DECLINED;
  }),

  /**
   * True when our current state is pointing to the 'active'(hired) status
   *
   * @type {Boolean}
   */
  _isHired: computed('applicationSimpleStatusFilter', function() {
    return this.applicationSimpleStatusFilter === APPLICATION.SIMPLE_STATUS.HIRED;
  }),

  /**
   * True when our current state is pointing to the 'pending' status
   *
   * @type {Boolean}
   */
  _isPending: computed('applicationSimpleStatusFilter', function() {
    return this.applicationSimpleStatusFilter === APPLICATION.SIMPLE_STATUS.PENDING;
  }),

  /**
   * True when the search component is activated
   *
   * @type {Boolean}
   * @private
   */
  _isSearchActivated: readOnly('state.isSearchActivated'),

  /**
   * True when the 'select all' checkbox has been clicked by the user.
   *
   * @type {Boolean}
   */
  _isSelectAllChecked: alias('state.isSelectAllChecked'),

  /**
   * True when we want to show a specific message for an empty user filter change. i.e. not the default filter
   *
   * @type {Boolean}
   */
  _isShowFilterSpecificEmptyMessage: computed('state.isFilterFavorites', function() {
    return this.get('state.isFilterFavorites');
  }),

  /**
   * True if we want to show loader
   *
   * @type {Boolean}
   * @private
   */
  _isShowSearchLoader: computed('state.isLoadingResult', 'state.applicationListItems.length', function() {
    return this.get('state.isLoadingResult') && !this.get('state.applicationListItems.length');
  }),

  /**
   * Will be true when we should show the select all option bar.
   *
   * @type {Boolean}
   */
  _isShowSelectAllOptions: readOnly('state.isShowSelectAllOptions'),

  /**
   * The total number of applications selected.
   *
   * @type {Number}
   */
  _numberSelectedText: computed('state.totalApplicationsSelected.[]', 'state.currentPagination', function() {
    let numberSelected  = this.get('state.totalApplicationsSelected.length'),
      pagination        = this.get('state.currentPagination'),
      resultsTotal      = pagination.get('total');

    if (this.get('state.isSearchActivated')) {
      resultsTotal      = this.get('state.applicationSearchResults.length');
    }

    if (numberSelected === resultsTotal) {
      return `All selected (${numberSelected})`;
    }

    return `${numberSelected} selected`;
  }),

  _applicationIdListItemMap: computed(
    'applicationListItems.[]',
  function() {
    return this.applicationListItems.reduce((accum, application) => {
      accum[application.id] = application;

      return accum;
    }, {});
  }),

  _featuredApplicationIdMap: computed(
    'featuredApplications.[]',
    '_applicationIdListItemMap',
  function() {
    const { _applicationIdListItemMap } = this;

    return this.featuredApplications.reduce((accum, application) => {
      accum[application.id] = _applicationIdListItemMap[application.id];

      return accum;
    }, {});
  }),

  _featuredApplicants: computed(
    'applicationListItems.[]',
    '_featuredApplicationIdMap',
  function() {
    const { _featuredApplicationIdMap } = this;

    return this.applicationListItems.filter(({ id }) => _featuredApplicationIdMap[id]);
  }),

  _nonfeaturedApplicants: computed(
    'applicationListItems.[]',
    '_featuredApplicationIdMap',
  function() {
    const { _featuredApplicationIdMap } = this;

    return this.applicationListItems.filter(({ id }) => !_featuredApplicationIdMap[id]);
  }),

  /**
   * Observe this select all checkbox.  If the user clicks, we need to update our state.
   */
  _observeIsSelectAllChecked: observer('_isSelectAllChecked', function() {
    this.get('stateManager').selectAllChanged();
  }),

  /**
   * Route name for the projects listings view.
   *
   * @type {String}
   */
  _projectsRoute: EMBER_ROUTE_NAMES.PROJECTS__IN_PROGRESS,

  sendMessagesTask: task(function *(message, threadIds) {
    try {
      yield this.stateManager.sendMessages(message, threadIds);
      this.loadApplications();
    } catch (error) {
      this.applicationStateManager.addToast('Message failed to send. Please try again.');
    }
  }).drop(),

  declineApplicationsTask: task(function *(appsSelected, message) {
    try {
      yield this.stateManager.declineApplications(appsSelected, message);
      this.loadApplications();
    } catch (error) {
      this.applicationStateManager.addToast('There was problem declining these proposals. Please try again.');
    }
  }).drop(),

  /**
   * @inheritDoc
   */
  actions: {
    addFavorite(application) {
      return this.applicationCommandService.addFavorite(application);
    },

    removeFavorite(application) {
      return this.applicationCommandService.removeFavorite(application);
    },

    hireCreator(application) {
      if (this.currentUser.company.isNeedsCreditCard) {
        return showModal(this, StripeCCModal, {
          seller: application.seller,
          application: application,
          onSuccess: () => this.hireCreator(),
        });
      }

      showModal(this, MakeOfferModal, {
        application,
        offerRecipientName: application.seller.name,
        initialPayoutAmount: 0.00,
        onPriceConfirmed: (price) => {
          this.stateManager.sendOffer.perform(application.id, price);
        },
      });
    },

    declineApplication(application) {
      showModal(this, DeclineApplicationModal, {
        title: `Decline ${application.seller.name}’s proposal`,
        textAreaPlaceholder: `Optional: Leave a note for ${application.seller.name}`,
        cancelText: 'Cancel',
        confirmText: 'Decline',
        onConfirmMessage: (message) => {
          this.declineApplicationsTask.perform([application.id], message);
        }
      });
    },

    undeclineApplication(application) {
      showModal(this, UndeclineApplicationModal, {
        seller: application.seller,
        application: application,
        undeclineApplication: () => this.stateManager.undeclineApplication(application.id).then(() => {
          this.applicationStateManager.addToast('Proposal un-declined');
        }).catch((e) => {
          this.applicationStateManager.addToast('There was a problem un-declining this proposal. Please try again.');
        }),
      });
    },

    handleProfileMediaClicked(projectdigest, e) {
      e.preventDefault();
      e.stopPropagation();

      showModal(this, LightboxOverlay, {
        digestsToShowForVideoCarousel: [projectdigest],
      });
    },

    onSelectAllToggled() {
      this.set('state.isSelectAllChecked', !this._isSelectAllChecked);
    },

    onCheckboxToggle({ id }) {
      const checked = this.state.totalApplicationsSelectedMap[id];

      // update storage of which are checked
      this.set('state.totalApplicationsSelectedMap', {
        ...this.state.totalApplicationsSelectedMap,
        [id]: !checked,
      });

      // enable 'select all' option
      this.stateManager.toggleApplicationSelected(id, !checked);
    },

    /**
     * Called when user clicks on decline on the multi-select bar.
     */
    onDeclineClicked: function() {
      let appsSelected = this.get('state.totalApplicationsSelected');

      if (appsSelected.length === 0) {
        showModal(this, ConfirmationModal, {
          titleText: 'No Creators Selected',
          bodyText: 'You must first select the creators you wish to decline',
          actionButtonText: 'Okay',
        });

        return;
      }

      let modalModel = {
        title: `Decline selected proposals`,
        textAreaPlaceholder: `Optional: Leave a note.`,
        cancelText: 'Cancel',
        confirmText: 'Decline',
        secondaryMessage: `This decline message will be sent to ${appsSelected.get('length')} freelancers`,
        onConfirmMessage: (message) => {
          this.declineApplicationsTask.perform(appsSelected, message);
        }
      };

      showModal(this, 'lsg/lsg-modal-text-editor', modalModel);
    },

    /**
     *
     */
    onDeclineAllClicked() {
      let appsToDeclineCount = this.get('state.applicationListItems.length'),
        secondaryMessageText = appsToDeclineCount === 1 ?
          `This decline message will be sent to ${appsToDeclineCount} freelancer` :
          `This decline message will be sent to ${appsToDeclineCount} freelancers`;

      let modalModel = {
        title: `Decline pending proposals`,
        textAreaPlaceholder: `Optional: Leave a note.`,
        cancelText: 'Cancel',
        confirmText: 'Decline',
        secondaryMessage: secondaryMessageText,
        onConfirmMessage: (message) => {
          let applicationIds = this.get('state.applicationListItems').map(application => application.get('id'));
          this.declineApplicationsTask.perform(applicationIds, message);
        }
      };

      showModal(this, 'lsg/lsg-modal-text-editor', modalModel);
    },

    /**
     *
     */
    onMessageAllClicked() {
      if (this.currentUser.isBuyer && this.currentUser.company.isNeedsCreditCard) {
        showModal(this, 'payments/stripe-cc-modal');
        return;
      }

      let appsToMessageCount = this.get('state.applicationListItems.length'),
        secondaryMessageText = appsToMessageCount === 1 ?
          `This message will be sent to ${appsToMessageCount} freelancer` :
          `This message will be sent to ${appsToMessageCount} freelancers`;

      let modalModel = {
        title: `Send message to pending proposals`,
        textAreaPlaceholder: `Enter your message`,
        cancelText: 'Cancel',
        confirmText: 'Send',
        secondaryMessage: secondaryMessageText,
        onConfirmMessage: (message) => {
          let threadIds = this.get('state.applicationListItems').map(application => {
            return application.get('thread.id');
          });

          this.sendMessagesTask.perform(message, threadIds);
        }
      };

      showModal(this, 'lsg/lsg-modal-text-editor', modalModel);
    },

    /**
     * Called when user clicks on send message on the multi-select bar.
     */
    onSendMessageClicked: function() {
      if (this.currentUser.isBuyer && this.currentUser.company.isNeedsCreditCard) {
        showModal(this, 'payments/stripe-cc-modal');
        return;
      }

      let appsSelected = this.get('state.totalApplicationsSelected'),
        company = this.get('store').peekRecord('company', this.get('currentUser.company.id')),
        companyBlacklistedUsers = company.get('blockedUsers'),
        freelancersNamesBlacklisted = A(),
        appsSelectedAfterBlacklist = A();

      if (appsSelected.length === 0) {
        showModal(this, ConfirmationModal, {
          titleText: 'No Creators Selected',
          bodyText: 'You must first select the creators you wish to message',
          actionButtonText: 'Okay',
        });

        return;
      }

      // Find any applications from blacklisted users and separate them into different lists.
      appsSelected.forEach((applicationSelectedId) => {
        let application = this.get('store').peekRecord('application', applicationSelectedId),
          seller = this.get('store').peekRecord('user', application.get('seller.id'));
        if (companyBlacklistedUsers.includes(seller)) {
          freelancersNamesBlacklisted.addObject(seller.get('fullName'));
        } else {
          appsSelectedAfterBlacklist.addObject(applicationSelectedId);
        }
      });

      //in this case, we've selected multiple apps, but they are all blacklisted, so we can't send any messages.
      if (!appsSelectedAfterBlacklist.length) {
        let data = {
          titleText: "All freelancers blocked",
          bodyText: `${freelancersNamesBlacklisted.join(', ')} are blocked by your team, so you can not message or hire them.`,
          actionButtonText: 'OK',
        };

        showModal(this, 'confirmation', data);
        return;
      }

      let secondaryMessage = `This message will be sent to ${appsSelectedAfterBlacklist.get('length')} freelancers`;
      if (freelancersNamesBlacklisted.length) {
        secondaryMessage += `<br/><br/>Some freelancers (${freelancersNamesBlacklisted.join(', ')}) are blacklisted by your team and can not be messaged.`;
      }

      let modalModel = {
        title: `Send message to selected freelancers`,
        textAreaPlaceholder: 'Enter your message',
        cancelText: 'Cancel',
        confirmText: 'Send',
        secondaryMessage: secondaryMessage,
        onConfirmMessage: (message) => {
          let threadIds = appsSelectedAfterBlacklist.map(applicationId => {
            let application = this.get('store').peekRecord('application', applicationId);
            return application.get('thread.id');
          });

          this.sendMessagesTask.perform(message, threadIds);
        }
      };

      showModal(this, 'lsg/lsg-modal-text-editor', modalModel);
    }
  }
});
